import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp, collection, getDocs } from "firebase/firestore";
import Spinner from './Spinner.js';
import useClickOutside from './hooks/useClickOutside.js';
import { useConfig } from './hooks/useConfig.js';
import { ArrowLeft, Edit2, CheckCircle, Save, Send, X, CreditCard, ClipboardCheck, Truck, UserCheck, AlertCircle, Clock, Loader2, ChevronDown, Printer, Paperclip, FileText, Eye } from 'lucide-react';
import MOCPreviewContent from './MOCPreviewContent.js';
import OrderPreview from './OrderPreview.js';
import { getSalesShippingSnapshot, getShippingSnapshotAddress } from './utils/shippingSnapshot.js';
import { updateDispatchAudit } from './utils/dispatchAudit.js';
import { updateOrderWithAudit } from './utils/orderAudit.js';

const getFileNameFromUrl = (url) => {
  if (!url) return '';
  try {
    const decodedUrl = decodeURIComponent(url);
    const pathParts = decodedUrl.split('?')[0].split('/');
    const fileNameWithPrefix = pathParts[pathParts.length - 1];
    const underscoreIndex = fileNameWithPrefix.indexOf('_');
    if (underscoreIndex !== -1) {
      if (/^\d{13,}/.test(fileNameWithPrefix)) {
        return fileNameWithPrefix.substring(underscoreIndex + 1);
      }
      const lastUnderscoreIndex = fileNameWithPrefix.lastIndexOf('_');
      if (lastUnderscoreIndex !== -1 && /\d{13,}/.test(fileNameWithPrefix.substring(lastUnderscoreIndex + 1))) {
        return fileNameWithPrefix.substring(0, lastUnderscoreIndex);
      }
    }
    return fileNameWithPrefix;
  } catch (e) {
    return 'Attached File';
  }
};

const OrderApprovalDetail = ({ db, user }) => {
  const { config } = useConfig();
  const { orderId } = useParams(); // This is the MOC (dispatch) ID
  const navigate = useNavigate();
  const [moc, setMoc] = useState(null);
  const [linkedOrder, setLinkedOrder] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [firms, setFirms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('moc');
  const printRef = useRef();

  const [showDropdown, setShowDropdown] = useState(false);

  // --- REJECTION STATE ---
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [isRejecting, setIsRejecting] = useState(false);

  // --- HOLD STATE ---
  const [showHoldModal, setShowHoldModal] = useState(false);
  const [holdReason, setHoldReason] = useState('');
  const [otherHoldReason, setOtherHoldReason] = useState('');
  const [isHolding, setIsHolding] = useState(false);

  const dropdownRef = useClickOutside(() => setShowDropdown(false));
  const salesShippingSnapshot = moc ? getSalesShippingSnapshot(moc, linkedOrder) : null;

  useEffect(() => {
    const fetchMoc = async () => {
      setLoading(true);
      try {
        const firmSnap = await getDocs(collection(db, "firms"));
        setFirms(firmSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

        const docRef = doc(db, "dispatches", orderId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const mocData = { id: docSnap.id, ...docSnap.data() };
          setMoc(mocData);

          if (mocData.orderId) {
            const orderSnap = await getDoc(doc(db, "orders", mocData.orderId));
            if (orderSnap.exists()) {
              const orderData = { id: orderSnap.id, ...orderSnap.data() };
              setLinkedOrder(orderData);

              if (orderData.customerID) {
                const custSnap = await getDoc(doc(db, "customers", orderData.customerID));
                setCustomer(custSnap.exists() ? { id: custSnap.id, ...custSnap.data() } : null);
              } else {
                setCustomer(null);
              }
            } else {
              setLinkedOrder(null);
              setCustomer(null);
            }
          } else {
            setLinkedOrder(null);
            setCustomer(null);
          }
        } else {
          alert("MOC not found");
          navigate('/accounts/approvals');
        }
      } catch (error) {
        console.error("Error fetching MOC:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchMoc();
  }, [db, orderId, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setMoc(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveDraft = async () => {
    setIsSaving(true);
    try {
      const auditTimestamp = new Date().toISOString();
      await updateDoc(doc(db, "dispatches", orderId), {
        ...moc,
        updatedAt: serverTimestamp()
      });
      await updateDispatchAudit(db, {
        dispatchId: orderId,
        dispatchData: {
          ...moc,
          dispatchStatus: moc.dispatchStatus
        },
        user,
        historyEvent: {
          action: 'finance_updated',
          dispatchStatus: moc.dispatchStatus,
          timestamp: auditTimestamp,
          remarks: moc.financeRemarks || ''
        }
      });
      alert("Finance details saved as draft.");
      setIsEditing(false);
    } catch (error) {
      alert("Error saving: " + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleApprove = async () => {
    if (!moc.paymentTerms || !moc.outstandingAmount || !moc.approvedByFinance) {
        alert("Please fill in Payment Terms, Outstanding Amount, and Verified By before approving.");
        return;
    }

    if (!window.confirm("Approve this MOC? Status will change to 'MOC Approved'.")) return;

    setIsSaving(true);
    try {
      const timestamp = serverTimestamp();
      const auditTimestamp = new Date().toISOString();
      // 1. Update Dispatch (MOC)
      await updateDoc(doc(db, "dispatches", orderId), {
        ...moc,
        dispatchStatus: 'moc_approved',
        financeApprovedAt: timestamp,
        updatedAt: timestamp
      });
      await updateDispatchAudit(db, {
        dispatchId: orderId,
        dispatchData: {
          ...moc,
          dispatchStatus: 'moc_approved'
        },
        user,
        historyEvent: {
          action: 'finance_approved',
          dispatchStatus: 'moc_approved',
          timestamp: auditTimestamp,
          remarks: moc.financeRemarks || ''
        }
      });

      // 2. Update Linked Order
      if (moc.orderId) {
        await updateOrderWithAudit(db, {
          orderId: moc.orderId,
          orderPayload: {
            status: 'moc_approved',
            updatedAt: auditTimestamp,
            updatedBy: user?.displayName || user?.email || 'Unknown',
            updatedByEmail: user?.email || ''
          },
          previousData: linkedOrder || {},
          user,
          historyEvent: {
            action: 'finance_approved',
            status: 'moc_approved',
            user,
            timestamp: auditTimestamp,
            remarks: moc.financeRemarks || ''
          }
        });
      }

      alert("MOC Approved successfully!");
      navigate('/accounts/approvals');
    } catch (error) {
      alert("Error approving: " + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRejectSubmit = async () => {
    if (!rejectionReason) {
      alert("Please select a rejection reason.");
      return;
    }
    if (rejectionReason === 'Other' && !otherReason.trim()) {
      alert("Please specify the other reason.");
      return;
    }

    const finalReason = rejectionReason === 'Other' ? otherReason : rejectionReason;

    if (!window.confirm(`Are you sure you want to reject this MOC?`)) return;

    setIsRejecting(true);
    try {
      const auditTimestamp = new Date().toISOString();
      // 1. Update Dispatch (MOC)
      await updateDoc(doc(db, "dispatches", orderId), {
        dispatchStatus: 'fin_rejected',
        finRejectionReason: finalReason,
        updatedAt: serverTimestamp()
      });
      await updateDispatchAudit(db, {
        dispatchId: orderId,
        dispatchData: {
          ...moc,
          dispatchStatus: 'fin_rejected'
        },
        user,
        historyEvent: {
          action: 'finance_rejected',
          dispatchStatus: 'fin_rejected',
          timestamp: auditTimestamp,
          remarks: finalReason
        }
      });

      // 2. Update Linked Order
      if (moc.orderId) {
        await updateOrderWithAudit(db, {
          orderId: moc.orderId,
          orderPayload: {
            status: 'fin_rejected',
            finRejectionReason: finalReason,
            updatedAt: auditTimestamp,
            updatedBy: user?.displayName || user?.email || 'Unknown',
            updatedByEmail: user?.email || ''
          },
          previousData: linkedOrder || {},
          user,
          historyEvent: {
            action: 'finance_rejected',
            status: 'fin_rejected',
            user,
            timestamp: auditTimestamp,
            remarks: finalReason
          }
        });
      }

      alert("MOC rejected successfully.");
      setShowRejectModal(false);
      setRejectionReason('');
      setOtherReason('');
      navigate('/accounts/approvals');
    } catch (error) {
      alert("Error rejecting MOC: " + error.message);
    } finally {
      setIsRejecting(false);
    }
  };

  const handleHoldSubmit = async () => {
    if (!holdReason) {
      alert("Please select a hold reason.");
      return;
    }
    if (holdReason === 'Other' && !otherHoldReason.trim()) {
      alert("Please specify the other reason.");
      return;
    }

    const finalReason = holdReason === 'Other' ? otherHoldReason : holdReason;

    if (!window.confirm(`Are you sure you want to put this MOC on Hold?`)) return;

    setIsHolding(true);
    try {
      const auditTimestamp = new Date().toISOString();
      // 1. Update Dispatch (MOC)
      await updateDoc(doc(db, "dispatches", orderId), {
        dispatchStatus: 'fin_hold',
        finHoldReason: finalReason,
        updatedAt: serverTimestamp()
      });
      await updateDispatchAudit(db, {
        dispatchId: orderId,
        dispatchData: {
          ...moc,
          dispatchStatus: 'fin_hold'
        },
        user,
        historyEvent: {
          action: 'finance_hold',
          dispatchStatus: 'fin_hold',
          timestamp: auditTimestamp,
          remarks: finalReason
        }
      });

      // 2. Update Linked Order
      if (moc.orderId) {
        await updateOrderWithAudit(db, {
          orderId: moc.orderId,
          orderPayload: {
            status: 'fin_hold',
            finHoldReason: finalReason,
            updatedAt: auditTimestamp,
            updatedBy: user?.displayName || user?.email || 'Unknown',
            updatedByEmail: user?.email || ''
          },
          previousData: linkedOrder || {},
          user,
          historyEvent: {
            action: 'finance_hold',
            status: 'fin_hold',
            user,
            timestamp: auditTimestamp,
            remarks: finalReason
          }
        });
      }

      alert("MOC put on hold successfully.");
      setShowHoldModal(false);
      setHoldReason('');
      setOtherHoldReason('');
      navigate('/accounts/approvals');
    } catch (error) {
      alert("Error putting MOC on hold: " + error.message);
    } finally {
      setIsHolding(false);
    }
  };

  const handlePrint = () => {
    const printContent = printRef.current.innerHTML;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>MOC ${moc.mocNo}</title>
          <style>
            body { font-family: Arial, sans-serif; color: #000; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
            td, th { border: 1px solid #000 !important; padding: 8px; text-align: left; font-size: 13px; }
            th { font-weight: bold; background-color: #f2f2f2; }
            @page { margin: 1cm; }
          </style>
        </head>
        <body>${printContent}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 250);
  };

  if (loading) return <Spinner />;

  // Check if current user is finance (accounts role)
  const isFinance = user?.role === 'accounts';

  const getSalesManagerApprovalTime = (orderData = {}) => {
    if (Array.isArray(orderData.salesReviewHistory)) {
      const approvalReview = [...orderData.salesReviewHistory]
        .reverse()
        .find(review => review?.status === 'in_que' && review?.reviewedAt);

      if (approvalReview?.reviewedAt) return approvalReview.reviewedAt;
    }

    return orderData.salesReviewedAt || '';
  };

  const salesManagerApprovedAt = getSalesManagerApprovalTime(linkedOrder || {});

  // Determine if we should show finance actions (only in initial pending/hold/rejected stages)
  const showFinanceActions = isFinance && ['moc_pending_finance', 'fin_hold', 'fin_rejected'].includes(moc?.dispatchStatus);

  // --- RENDER PREVIEW MODE (PDF Layout) ---
  const renderPreview = () => (
    <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
      <MOCPreviewContent
        mocData={{
          ...moc,
          salesManagerApprovedAt
        }}
        printRef={printRef}
      />
    </div>
  );

  // --- RENDER EDIT MODE (Form Layout) ---
  const renderEdit = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Card 3: Finance Details (EDITABLE) */}
      <div style={{ ...cardStyle, border: '2px solid #2563eb' }}>
        <h3 style={{ ...sectionHeaderStyle, color: '#2563eb' }}><CreditCard size={18} /> Finance Details (Editable)</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
            <div>
                <label style={labelStyle}>Payment Terms</label>
                <input name="paymentTerms" value={moc.paymentTerms} onChange={handleChange} style={inputStyle} placeholder="e.g. 30 Days Credit" />
            </div>
            <div>
                <label style={labelStyle}>Payment Status</label>
                <select name="paymentStatus" value={moc.paymentStatus} onChange={handleChange} style={inputStyle}>
                    <option value="">Select Status...</option>
                    <option value="Received">Received</option>
                    <option value="Credit">Credit</option>
                    <option value="Old Payment Received">Old Payment Received</option>
                </select>
            </div>
            <div>
                <label style={labelStyle}>Verified By (Finance)</label>
                <select name="approvedByFinance" value={moc.approvedByFinance} onChange={handleChange} style={inputStyle}>
                    <option value="">Select Staff...</option>
                    {config.finance_verified_by.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '20px' }}>
            <div><label style={labelStyle}>Last Payment Amount</label><input type="number" name="lastPaymentAmount" value={moc.lastPaymentAmount} onChange={handleChange} style={inputStyle} /></div>
            <div><label style={labelStyle}>Payment Rec. Date</label><input type="date" name="paymentRecDate" value={moc.paymentRecDate} onChange={handleChange} style={inputStyle} /></div>
            <div><label style={labelStyle}>Outstanding Amount</label><input type="number" name="outstandingAmount" value={moc.outstandingAmount} onChange={handleChange} style={inputStyle} /></div>
            <div><label style={labelStyle}>Outstanding Due Date</label><input type="date" name="outstandingDue" value={moc.outstandingDue || ''} onChange={handleChange} style={inputStyle} /></div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '20px' }}>
            <div><label style={labelStyle}>Outstanding Days</label><input type="number" name="outstandingDays" value={moc.outstandingDays} onChange={handleChange} style={inputStyle} /></div>
            <div>
                <label style={labelStyle}>Finance Remarks</label>
                <textarea name="financeRemarks" value={moc.financeRemarks} onChange={handleChange} style={{ ...inputStyle, minHeight: '80px' }} placeholder="Internal notes..." />
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        {/* Card 1: Document Details (ReadOnly) */}
        <div style={cardStyle}>
          <h3 style={sectionHeaderStyle}>Document Details (Read-Only)</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <div><label style={labelStyle}>MOC No.</label><input type="text" value={moc.mocNo} style={{...inputStyle, backgroundColor: '#f8fafc'}} disabled /></div>
            <div><label style={labelStyle}>MOC Date</label><input type="text" value={moc.mocDate} style={{...inputStyle, backgroundColor: '#f8fafc'}} disabled /></div>
            <div style={{ gridColumn: 'span 2' }}><label style={labelStyle}>Customer</label><input type="text" value={moc.customerName} style={{...inputStyle, backgroundColor: '#f8fafc'}} disabled /></div>
          </div>
        </div>

        {/* Card 2: Location (ReadOnly) */}
        <div style={cardStyle}>
          <h3 style={sectionHeaderStyle}>Location & Logistics (Read-Only)</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <div><label style={labelStyle}>Warehouse</label><input type="text" value={moc.warehouse} style={{...inputStyle, backgroundColor: '#f8fafc'}} disabled /></div>
            <div><label style={labelStyle}>Shipping Address</label><textarea value={getShippingSnapshotAddress(salesShippingSnapshot, moc.shippingAddress)} style={{...inputStyle, backgroundColor: '#f8fafc', minHeight: '60px'}} disabled /></div>
          </div>
        </div>
      </div>

      {/* Card: BOM Summary (Read-Only) */}
      <div style={cardStyle}>
        <h3 style={sectionHeaderStyle}>BOM / Components (Read-Only)</h3>
        <table style={tableStyle}>
            <thead>
                <tr style={{ backgroundColor: '#f9fafb' }}>
                    <th style={thStyle}>S.No.</th>
                    <th style={thStyle}>SKU</th>
                    <th style={{ ...thStyle, textAlign: 'left' }}>Component Name</th>
                    <th style={thStyle}>New Qty</th>
                    <th style={thStyle}>Rental Qty</th>
                    <th style={thStyle}>Weight</th>
                </tr>
            </thead>
            <tbody>
                {moc.components?.map((c, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={tdStyle}>{idx + 1}</td>
                        <td style={tdStyle}>{c.sku}</td>
                        <td style={{ ...tdStyle, textAlign: 'left' }}>{c.name}</td>
                        <td style={tdStyle}>{c.newQty || 0}</td>
                        <td style={tdStyle}>{c.rentalQty || 0}</td>
                        <td style={tdStyle}>{c.weight} kg</td>
                    </tr>
                ))}
                <tr style={{ backgroundColor: '#f9fafb', fontWeight: 'bold' }}>
                    <td colSpan="5" style={{ ...tdStyle, textAlign: 'right' }}>TOTAL WEIGHT</td>
                    <td style={tdStyle}>{moc.totalWeight?.toFixed(2)} kg</td>
                </tr>
            </tbody>
        </table>
      </div>
    </div>
  );

  const renderOrderAttachments = () => {
    const attachmentSource = linkedOrder || moc;

    if (!attachmentSource?.quoteFileUrl && !attachmentSource?.poFileUrl && !attachmentSource?.workOrderFileUrl) {
      return null;
    }

    return (
      <div style={{ ...cardStyle, marginTop: '20px' }}>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '0 0 20px 0' }}>
          <Paperclip size={20} color="#2563eb" /> Order Attachments
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
          {attachmentSource.quoteFileUrl && (
            <a href={attachmentSource.quoteFileUrl} target="_blank" rel="noopener noreferrer" style={attachmentCardStyle}>
              <div style={attachmentIconStyle}><FileText size={20} /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={attachmentLabelStyle}>Quote Document</div>
                <div style={attachmentNameStyle}>{getFileNameFromUrl(attachmentSource.quoteFileUrl)}</div>
              </div>
              <Eye size={18} color="#6b7280" />
            </a>
          )}
          {attachmentSource.poFileUrl && (
            <a href={attachmentSource.poFileUrl} target="_blank" rel="noopener noreferrer" style={attachmentCardStyle}>
              <div style={{ ...attachmentIconStyle, backgroundColor: '#dcfce7', color: '#166534' }}><FileText size={20} /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={attachmentLabelStyle}>Purchase Order (PO)</div>
                <div style={attachmentNameStyle}>{getFileNameFromUrl(attachmentSource.poFileUrl)}</div>
              </div>
              <Eye size={18} color="#6b7280" />
            </a>
          )}
          {attachmentSource.workOrderFileUrl && (
            <a href={attachmentSource.workOrderFileUrl} target="_blank" rel="noopener noreferrer" style={attachmentCardStyle}>
              <div style={{ ...attachmentIconStyle, backgroundColor: '#fef9c3', color: '#854d0e' }}><FileText size={20} /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={attachmentLabelStyle}>Work Order</div>
                <div style={attachmentNameStyle}>{getFileNameFromUrl(attachmentSource.workOrderFileUrl)}</div>
              </div>
              <Eye size={18} color="#6b7280" />
            </a>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
      {/* Dynamic Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '25px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button onClick={() => navigate('/accounts/approvals')} style={backBtnStyle}>
            <ArrowLeft size={24} />
          </button>
          <h2 style={{ margin: 0 }}>{isEditing ? 'Edit Finance Details' : (!showFinanceActions ? `MOC Record: ${moc?.mocNo || ''}` : `MOC Approval Preview: ${moc?.mocNo || ''}`)}</h2>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
            {showFinanceActions ? (
                isEditing ? (
                    <>
                        <button onClick={() => setIsEditing(false)} style={secondaryBtnStyle}>Cancel</button>
                        <button onClick={handleSaveDraft} disabled={isSaving} style={draftBtnStyle}><Save size={18}/> Save Draft</button>
                        <button onClick={handleApprove} disabled={isSaving} style={confirmBtnStyle}><Send size={18}/> Approve MOC</button>
                    </>
                ) : (
                    <>
                        <button onClick={() => setIsEditing(true)} style={editBtnStyle}><Edit2 size={18}/> Edit Finance Details</button>
                        <button onClick={handleApprove} disabled={isSaving} style={confirmBtnStyle}><CheckCircle size={18}/> Approve MOC</button>
                        <div style={{ position: 'relative' }} ref={dropdownRef}>
                          <button onClick={() => setShowDropdown(!showDropdown)} style={secondaryBtnStyle}>
                            More <ChevronDown size={18} />
                          </button>
                          {showDropdown && (
                            <div style={{
                              position: 'absolute',
                              top: 'calc(100% + 5px)',
                              right: 0,
                              backgroundColor: 'white',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                              width: '150px',
                              zIndex: 1000
                            }}>
                              <div onClick={() => { setShowRejectModal(true); setShowDropdown(false); }} style={{ padding: '10px 15px', cursor: 'pointer', color: '#ef4444', fontWeight: 'bold', borderBottom: '1px solid #f3f4f6' }}>
                                Reject MOC
                              </div>
                              <div onClick={() => { setShowHoldModal(true); setShowDropdown(false); }} style={{ padding: '10px 15px', cursor: 'pointer', color: '#f59e0b', fontWeight: 'bold' }}>
                                Hold MOC
                              </div>
                            </div>
                          )}
                        </div>
                    </>
                )
            ) : ( 
                <button onClick={handlePrint} style={confirmBtnStyle}><Printer size={18}/> Export PDF</button>
            )}
        </div>
      </div>

      {isEditing ? renderEdit() : (
        <>
          <div style={tabCardStyle}>
            <div style={tabContainerStyle}>
              <button
                style={activeTab === 'moc' ? activeTabStyle : tabStyle}
                onClick={() => setActiveTab('moc')}
              >
                MOC Preview
              </button>
              {linkedOrder && (
                <button
                  style={activeTab === 'order' ? activeTabStyle : tabStyle}
                  onClick={() => setActiveTab('order')}
                >
                  Order Preview
                </button>
              )}
            </div>

            <div style={tabContentStyle}>
              {activeTab === 'order' ? (
                customer ? <OrderPreview order={linkedOrder} customer={customer} firms={firms} db={db} user={user} /> : <Spinner />
              ) : (
                <>
                  {renderPreview()}
                  {renderOrderAttachments()}
                </>
              )}
            </div>
          </div>
        </>
      )}

      {/* REJECTION MODAL */}
      {showRejectModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}>
          <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '10px', width: '400px', maxWidth: '90%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#dc2626' }}>Reject MOC</h3>
              <button onClick={() => { setShowRejectModal(false); setRejectionReason(''); setOtherReason(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            
            <p style={{ fontSize: '14px', color: '#374151', marginBottom: '15px' }}>
              Please select a reason for rejecting this MOC. It will be sent back to the Sales team.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
              {["Pending Payment", "Credit Limit Exceeded", "Document Missing", "Other"].map((reason) => (
                <label key={reason} style={radioLabelStyle}>
                  <input 
                    type="radio" 
                    name="rejectionReason" 
                    value={reason} 
                    checked={rejectionReason === reason} 
                    onChange={(e) => setRejectionReason(e.target.value)} 
                  />
                  {reason}
                </label>
              ))}
              
              {rejectionReason === 'Other' && (
                <textarea 
                  placeholder="Please specify the reason..." 
                  value={otherReason} 
                  onChange={(e) => setOtherReason(e.target.value)}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px', minHeight: '80px', marginTop: '10px', boxSizing: 'border-box' }}
                />
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={() => { setShowRejectModal(false); setRejectionReason(''); setOtherReason(''); }} style={draftBtnStyle}>Cancel</button>
              <button onClick={handleRejectSubmit} disabled={isRejecting} style={{...confirmBtnStyle, backgroundColor: '#dc2626'}}>
                {isRejecting ? <Loader2 className="animate-spin" size={16} /> : 'Confirm Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* HOLD MODAL */}
      {showHoldModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}>
          <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '10px', width: '400px', maxWidth: '90%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#f59e0b' }}>Hold MOC</h3>
              <button onClick={() => { setShowHoldModal(false); setHoldReason(''); setOtherHoldReason(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            
            <p style={{ fontSize: '14px', color: '#374151', marginBottom: '15px' }}>
              Please select a reason for placing this MOC on hold.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
              {["Awaiting Payment Confirmation", "Pending Document Upload", "Other"].map((reason) => (
                <label key={reason} style={radioLabelStyle}>
                  <input 
                    type="radio" 
                    name="holdReason" 
                    value={reason} 
                    checked={holdReason === reason} 
                    onChange={(e) => setHoldReason(e.target.value)} 
                  />
                  {reason}
                </label>
              ))}
              
              {holdReason === 'Other' && (
                <textarea 
                  placeholder="Please specify the hold reason..." 
                  value={otherHoldReason} 
                  onChange={(e) => setOtherHoldReason(e.target.value)}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px', minHeight: '80px', marginTop: '10px', boxSizing: 'border-box' }}
                />
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={() => { setShowHoldModal(false); setHoldReason(''); setOtherHoldReason(''); }} style={draftBtnStyle}>Cancel</button>
              <button onClick={handleHoldSubmit} disabled={isHolding} style={{...confirmBtnStyle, backgroundColor: '#f59e0b'}}>
                {isHolding ? <Loader2 className="animate-spin" size={16} /> : 'Confirm Hold'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// --- STYLES ---
const cardStyle = { backgroundColor: 'white', padding: '20px', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' };
const sectionHeaderStyle = { fontSize: '16px', fontWeight: 'bold', borderBottom: '1px solid #f3f4f6', paddingBottom: '10px', marginBottom: '15px', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' };
const labelStyle = { display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '6px', textTransform: 'uppercase' };
const inputStyle = { width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box' };
const radioLabelStyle = { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' };
const btnStyle = { padding: '10px 20px', borderRadius: '8px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', border: 'none' };
const confirmBtnStyle = { ...btnStyle, backgroundColor: '#059669', color: 'white' };
const editBtnStyle = { ...btnStyle, backgroundColor: '#2563eb', color: 'white' };
const draftBtnStyle = { ...btnStyle, backgroundColor: 'white', color: '#374151', border: '1px solid #d1d5db' };
const secondaryBtnStyle = { ...btnStyle, backgroundColor: 'white', color: '#64748b', border: '1px solid #e2e8f0' };
const backBtnStyle = { background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' };

const tableStyle = { width: '100%', borderCollapse: 'collapse', marginTop: '10px' };
const thStyle = { padding: '12px 10px', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #f3f4f6' };
const tdStyle = { padding: '12px 10px', fontSize: '13px', color: '#374151', textAlign: 'center' };
const attachmentCardStyle = { display: 'flex', alignItems: 'center', gap: '12px', padding: '14px', border: '1px solid #e5e7eb', borderRadius: '10px', textDecoration: 'none', color: 'inherit', backgroundColor: '#fff' };
const attachmentIconStyle = { width: '42px', height: '42px', borderRadius: '10px', backgroundColor: '#eff6ff', color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 };
const attachmentLabelStyle = { fontSize: '13px', fontWeight: '600', color: '#374151' };
const attachmentNameStyle = { fontSize: '11px', color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' };
const tabCardStyle = { backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden' };
const tabContainerStyle = { display: 'flex', gap: '10px', padding: '15px 20px', borderBottom: '1px solid #e5e7eb', backgroundColor: '#f8fafc' };
const tabStyle = { padding: '10px 16px', borderRadius: '8px', border: '1px solid transparent', backgroundColor: 'transparent', color: '#64748b', fontWeight: '600', cursor: 'pointer' };
const activeTabStyle = { ...tabStyle, backgroundColor: '#eff6ff', borderColor: '#bfdbfe', color: '#1d4ed8' };
const tabContentStyle = { padding: '20px' };

export default OrderApprovalDetail;
